<script setup lang="ts">
import LoadingComponent from '@/components/LoadingComponent.vue'
import Card from 'primevue/card'
import CustomButton from '@/components/CustomComponent/CustomButton.vue'
import { ref } from 'vue'
import { useToast } from 'primevue/usetoast'
import ApiService from '@/api/ApiService'
import type { ToastMessageOptions } from 'primevue/toast'
import ConversionUtility from '@/utils/ConversionUtility'
import * as XLSX from 'xlsx'
import ProgressBar from 'primevue/progressbar'
import type GetAllPigmyReportsReq from '@/api/models/reports/GetAllPigmyReportsReq'
import type AllPigmyReportsData from '@/api/models/reports/AllPigmyReportsData'
import Divider from 'primevue/divider'
import FloatLabel from 'primevue/floatlabel'
import DatePicker from 'primevue/calendar'
import ToggleSwitch from 'primevue/inputswitch'
import Dialog from 'primevue/dialog'
import Toast from 'primevue/toast'

const loadingProgress = ref<number>()
const toast = useToast()
const isLoading = ref<boolean>(false)
const nextPageKey = ref(null)
const minDate = ref(new Date(2024, 8, 1))
const maxDate = ref(new Date())
const fromDate = ref<Date>()
const toDate = ref<Date | null>()
const FDate = ref()
const EDate = ref()
const isClosed = ref(false)
const pigmyReports = ref<AllPigmyReportsData[]>([])
const allPigmyReports = ref<AllPigmyReportsData[]>([])
const isAllDataLoaded = ref<boolean>(false)

// Load all pigmy reports without date filters for the 'Download All' button
function loadAllPigmyReports() {
  isLoading.value = true
  allPigmyReports.value = []
  isAllDataLoaded.value = false
  
  fetchAllPigmyReports(0)
}

function fetchAllPigmyReports(nextKey: number) {
  const requestParam: GetAllPigmyReportsReq = {
    startDate: null,
    endDate: null,
    isClosed: null,
    nextPageKey: nextKey
  }

  ApiService.getAllPigmyReports(requestParam)
    .then((response) => {
      if (response.statusCode == 200) {
        allPigmyReports.value.push(...response.data.data)
        if (response.data.nextPageKey != null) {
          fetchAllPigmyReports(response.data.nextPageKey)
        } else {
          isAllDataLoaded.value = true
          showToast('Success', 'All pigmy reports loaded successfully', 'success')
        }
      } else {
        showToast('Error', 'No customers found', 'error')
      }
    })
    .catch((error) => {
      showToast('Error', error, 'error')
    })
    .finally(() => {
      if (isAllDataLoaded.value) {
        isLoading.value = false
      }
    })
}

// Get filtered pigmy reports based on date range
function getPigmyReports(nextKey: number) {
  if (!fromDate.value || !toDate.value) {
    showToast('Date Selection Required', 'Please select both From Date and To Date to download filtered reports', 'warn')
    return
  }

  isLoading.value = true
  pigmyReports.value = []

  const requestParam: GetAllPigmyReportsReq = {
    startDate: fromDate.value ? new Date(fromDate.value).setHours(0, 0, 0, 0) : null,
    endDate: toDate.value ? new Date(toDate.value).setHours(23, 59, 59, 999) : null,
    isClosed: isClosed.value ? isClosed.value : null,
    nextPageKey: nextKey
  }

  ApiService.getAllPigmyReports(requestParam)
    .then((response) => {
      if (response.statusCode == 200) {
        pigmyReports.value.push(...response.data.data)
        if (response.data.nextPageKey != null) {
          getPigmyReports(response.data.nextPageKey)
        }
        if (response.data.nextPageKey == null) {
          downloadReport(pigmyReports.value)
        }
      } else {
        showToast('Error', 'No customers found', 'error')
      }
    })
    .catch((error) => {
      showToast('Error', error, 'error')
    })
    .finally(() => {
      isLoading.value = false
    })
}

// Download all pigmy reports
function downloadAllReports() {
  if (!isAllDataLoaded.value) {
    loadAllPigmyReports()
    // Wait for data to load before downloading
    const checkInterval = setInterval(() => {
      if (isAllDataLoaded.value) {
        clearInterval(checkInterval)
        downloadReport(allPigmyReports.value, true)
      }
    }, 1000)
    return
  }
  
  downloadReport(allPigmyReports.value, true)
}

// Generic download report function that works with any data source
function downloadReport(reportData: AllPigmyReportsData[], isAllData: boolean = false) {
  loadingProgress.value = 25
  isLoading.value = true

  const reportTitle = isAllData ? 'All Pigmy Reports' : 'Filtered Pigmy Reports'
  const reportDataDownload = [
    ['Bhavasar Patini Sahakar Sanga', '', '', '', '', '', '', '', '', '', ''],
    ['Tq: Badami, Dis: Bagalkot', '', '', '', '', '', '', '', '', '', ''],
    [],
    [reportTitle],
    ['Total', reportData.length],
    [],
    [
      'Sl No',
      'Customer Name',
      'Pigmy ID',
      'Customer ID',
      'Pigmy Amount',
      'Balance',
      'Interest Amount',
      'Penalty Amount',
      'SB ID',
      'Created On',
      'Status'
    ]
  ]

  let index = 0
  reportData?.forEach((pigmyReport) => {
    index = index + 1
    reportDataDownload.push([
      index,
      pigmyReport.customerName,
      pigmyReport.id,
      pigmyReport.cusId,
      ConversionUtility.toRuppesCoversion(pigmyReport.pigmyAmount),
      ConversionUtility.toRuppesCoversion(pigmyReport.balance),
      ConversionUtility.toRuppesCoversion(pigmyReport.interestAmount),
      ConversionUtility.toRuppesCoversion(pigmyReport.penaltyAmount),
      pigmyReport.transferredSBId,
      ConversionUtility.toDateFormat(pigmyReport.createdAt),
      pigmyReport.status ? 'Closed' : 'Open'
    ])
  })

  const worksheet = XLSX.utils.aoa_to_sheet(reportDataDownload)

  // Merge cells to center the bank name and location
  worksheet['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 10 } }, // Merge A1:K1 for Bank Name
    { s: { r: 1, c: 0 }, e: { r: 1, c: 10 } } // Merge A2:K2 for Location
  ]

  // Apply styles for bank name and location
  if (worksheet['A1'] && worksheet['A2']) {
    worksheet['A1'].s = {
      font: { bold: true, sz: 18 },
      alignment: { horizontal: 'center', vertical: 'center' }
    }
    worksheet['A2'].s = {
      font: { bold: true, sz: 16 },
      alignment: { horizontal: 'center', vertical: 'center' }
    }
  }

  worksheet['!cols'] = [
    { wch: 5 },
    { wch: 25 },
    { wch: 15 },
    { wch: 15 },
    { wch: 20 },
    { wch: 15 },
    { wch: 20 },
    { wch: 20 },
    { wch: 15 },
    { wch: 15 },
    { wch: 10 }
  ]
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1')
  const workbookArrayBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
  const blob = new Blob([workbookArrayBuffer], { type: 'application/octet-stream' })

  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  const fileName = isAllData ? 
    `Complete_Pigmy_Report_${ConversionUtility.toDateFormat(Date.now())}.xlsx` : 
    `Filtered_Pigmy_Report_${ConversionUtility.toDateFormat(Date.now())}.xlsx`
  link.setAttribute('download', fileName)
  document.body.appendChild(link)
  link.click()

  isLoading.value = false
  showToast('Success', 'Report has been downloaded', 'success')
}

function showToast(summary: string, detail: string, severity: ToastMessageOptions['severity']) {
  toast.add({
    severity: severity,
    summary: summary,
    detail: detail,
    life: 5000
  })
}
</script>

<template>
  <Toast class="error-p-toast" />
  <main class="main">
    <Card class="search-card">
      <template #title>
        <h2 class="title-right text-3xl font-bold">All Pigmy Report</h2>
      </template>
      <template #content>
        <Divider />
        <div class="card-container">
          <div class="filter-section">
            <h3 class="section-title">Filter Reports</h3>
            <div class="form-grid">
              <div class="form-field custom-float-label">
                <span class="p-float-label">
                  <DatePicker
                    v-model="fromDate"
                    inputId="fromDate"
                    class="w-full"
                    :min-date="minDate"
                    :max-date="maxDate"
                    dateFormat="dd/mm/yy"
                    showIcon
                  />
                  <label for="fromDate">From Date</label>
                </span>
              </div>
              <div class="form-field custom-float-label">
                <span class="p-float-label">
                  <DatePicker
                    v-model="toDate"
                    inputId="toDate"
                    class="w-full"
                    :min-date="fromDate"
                    :max-date="maxDate"
                    dateFormat="dd/mm/yy"
                    showIcon
                  />
                  <label for="toDate">To Date</label>
                </span>
              </div>

              <div class="toggle-container">
                <label for="isClosed">Show only closed accounts</label>
                <ToggleSwitch v-model="isClosed" id="isClosed" class="blue-toggle" />
              </div>
              <CustomButton 
                title="Download Filtered" 
                @click="getPigmyReports(0)" 
                class="download-btn" 
                icon="pi pi-download"
                :disabled="!fromDate || !toDate"
              />
            </div>
          </div>
          
          <Divider>
            <span class="divider-text">OR</span>
          </Divider>
          
          <div class="download-all-section">
            <h3 class="section-title">Download Complete Report</h3>
            <p class="section-description">Download all pigmy reports without any filters</p>
            <CustomButton 
              title="Download All Reports" 
              @click="downloadAllReports()" 
              class="download-all-btn" 
              icon="pi pi-cloud-download"
            />
          </div>
        </div>

        <Dialog
          :visible="isLoading"
          modal
          :draggable="false"
          :closable="false"
          style="width: 60vw"
          header="Processing Data"
        >
          <div class="dialog-content">
            <p class="dialog-message">Please wait while we prepare your report...</p>
            <ProgressBar mode="indeterminate" style="height: 8px" class="custom-progress"></ProgressBar>
          </div>
        </Dialog>
      </template>
    </Card>
  </main>
</template>
<style>
.card-container {
  padding: 1rem;
  width: 100%;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #2c3e50;
}

.section-description {
  color: #7f8c8d;
  margin-bottom: 1rem;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
  width: 100%;
}

.form-field {
  width: 100%;
  margin-bottom: 1rem;
}

/* Custom float label styles */
.custom-float-label .p-float-label {
  display: block;
  position: relative;
}

.custom-float-label .p-float-label label {
  position: absolute;
  top: -10px;
  left: 10px;
  background-color: white;
  padding: 0 5px;
  font-size: 0.85rem;
  color: #666;
  z-index: 1;
}

.toggle-container {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0;
}

/* Toggle switch blue color */
.blue-toggle.p-inputswitch.p-inputswitch-checked .p-inputswitch-slider {
  background-color: #3B82F6 !important;
}

.blue-toggle.p-inputswitch:not(.p-disabled):hover.p-inputswitch-checked .p-inputswitch-slider {
  background-color: #2563EB !important;
}

.download-btn, .download-all-btn {
  width: 100%;
  margin-top: 0.5rem;
  height: 2.5rem;
}

.download-btn {
  font-size: 0.9rem;
  padding: 0.5rem 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.download-btn .p-button-icon {
  font-size: 0.9rem;
  margin-right: 0.5rem;
}

.download-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.download-all-btn {
  background-color: #2980b9;
  border-color: #2980b9;
}

.download-all-btn:hover {
  background-color: #3498db;
  border-color: #3498db;
}

.divider-text {
  background-color: white;
  padding: 0 1rem;
  font-weight: bold;
  color: #7f8c8d;
}

.download-all-section {
  background-color: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  margin-top: 1rem;
  text-align: center;
  width: 100%;
}

.dialog-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem;
}

.dialog-message {
  margin-bottom: 1.5rem;
  font-size: 1.1rem;
  color: #2c3e50;
}

.custom-progress {
  width: 100%;
  border-radius: 4px;
  overflow: hidden;
}

.search-card {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
}

.main {
  padding: 15px;
  max-width: 100%;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
}

.title-right {
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.filter-section {
  width: 100%;
}
</style>
